import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { BarChart3, BookOpen } from 'lucide-react';
import {
  attachOrganizationToNetwork,
  getConsultantNetworks,
  getConsultantOrganization,
  getConsultantOrganizations,
} from './api';
import OrganizationDetailHeader from '../organization/OrganizationDetailHeader';
import OrganizationInfoSection from '../organization/OrganizationInfoSection';
import OrganizationBillingSection from '../organization/OrganizationBillingSection';
import OrganizationNotesSection from '../organization/OrganizationNotesSection';
import OrganizationRelationshipsSection from '../organization/OrganizationRelationshipsSection';
import { useUser } from '../../contexts/UserContext';
import { getUserRoles } from '../../utils/authRoles';
import {
  fetchOrganizationSurveysPayload,
  fetchWorkbookAvailability,
  normalizeOrganizationSurveys,
} from '../organization/organizationSurveysApi';

const uniqueUsers = (users) =>
  users.filter((candidate, index, collection) => {
    if (!candidate) return false;
    const candidateKey = candidate.id != null ? `id:${candidate.id}` : `email:${candidate.email || index}`;

    return (
      collection.findIndex((entry, entryIndex) => {
        if (!entry) return false;
        const entryKey = entry.id != null ? `id:${entry.id}` : `email:${entry.email || entryIndex}`;
        return entryKey === candidateKey;
      }) === index
    );
  });

const hasReportableSurveys = (payload) =>
  normalizeOrganizationSurveys(payload).some((survey) =>
    Boolean(survey?.organization_survey_id ?? survey?.id)
  );

const ConsultantOrganizationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useUser();
  const isConsultantAdmin = getUserRoles(user).includes('consultant_admin');

  const [organization, setOrganization] = useState(null);
  const [scopedOrganization, setScopedOrganization] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasWorkbook, setHasWorkbook] = useState(false);
  const [hasResults, setHasResults] = useState(false);

  const [attachForm, setAttachForm] = useState({ organization_id: '', network_id: '' });
  const [isSubmittingAttach, setIsSubmittingAttach] = useState(false);

  const attachModal = useDisclosure();

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [
          organizationPayload,
          scopedOrganizations,
          networksPayload,
          workbookAvailable,
          surveysPayload,
        ] = await Promise.all([
          getConsultantOrganization(id),
          getConsultantOrganizations(),
          getConsultantNetworks(),
          isConsultantAdmin ? fetchWorkbookAvailability(id).catch(() => false) : false,
          isConsultantAdmin ? fetchOrganizationSurveysPayload({ organizationId: id }).catch(() => null) : null,
        ]);
        const scoped = (scopedOrganizations || []).find((entry) => String(entry?.id) === String(id)) || null;
        setOrganization(organizationPayload);
        setScopedOrganization(scoped);
        setNetworks(networksPayload || []);
        setHasWorkbook(workbookAvailable);
        setHasResults(hasReportableSurveys(surveysPayload));
        setAttachForm((prev) => ({ ...prev, organization_id: String(id) }));
      } catch (error) {
        console.error('Error loading consultant organization:', error);
        toast({
          title: 'Could not load school',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        navigate('/app/consultant/organizations');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [id, isConsultantAdmin, navigate, toast]);

  const organizationNetworks = useMemo(
    () => (Array.isArray(scopedOrganization?.networks) ? scopedOrganization.networks : []),
    [scopedOrganization]
  );
  const organizationUsers = useMemo(() => {
    return uniqueUsers([
      ...(Array.isArray(scopedOrganization?.users) ? scopedOrganization.users : []),
      scopedOrganization?.user,
      ...(Array.isArray(organization?.users) ? organization.users : []),
      organization?.user,
    ]);
  }, [organization, scopedOrganization]);
  const hasNetwork = organizationNetworks.length > 0 || Number(scopedOrganization?.network_count || 0) > 0;
  const canAttachToNetwork = !hasNetwork;

  const handleAttachOrganization = async (event) => {
    event.preventDefault();
    setIsSubmittingAttach(true);
    try {
      await attachOrganizationToNetwork(attachForm.network_id, attachForm.organization_id);
      toast({ title: 'School attached', status: 'success', duration: 3000, isClosable: true });
      attachModal.onClose();
      const [scopedOrganizations, networksPayload] = await Promise.all([
        getConsultantOrganizations(),
        getConsultantNetworks(),
      ]);
      const scoped = (scopedOrganizations || []).find((entry) => String(entry?.id) === String(id)) || null;
      setScopedOrganization(scoped);
      setNetworks(networksPayload || []);
      setAttachForm((prev) => ({ ...prev, network_id: '' }));
    } catch (error) {
      console.error('Error attaching organization:', error);
      toast({ title: 'Could not attach school', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsSubmittingAttach(false);
    }
  };

  if (isLoading) {
    return (
      <HStack justify="center" py={12}>
        <Spinner size="lg" />
      </HStack>
    );
  }

  if (!organization) {
    return null;
  }

  return (
    <Box p={{ base: 4, md: 6 }} maxW="6xl" mx="auto">
      <OrganizationDetailHeader
        title={organization.name}
        subtitle="Consultant school details"
        actions={[
          { label: 'Back to schools', to: '/app/consultant/organizations', variant: 'ghost' },
          { label: 'Edit school', to: `/app/consultant/organizations/${id}/edit`, variant: 'outline' },
          ...(isConsultantAdmin && hasWorkbook ? [{
            label: 'View Workbook',
            to: `/app/consultant/organizations/${id}/workbook`,
            leftIcon: <BookOpen size={16} />,
            variant: 'outline',
            colorScheme: 'purple',
          }] : []),
          ...(isConsultantAdmin && hasResults ? [{
            label: 'Survey Results',
            to: `/app/consultant/organizations/${id}/results`,
            leftIcon: <BarChart3 size={16} />,
            variant: 'outline',
            colorScheme: 'green',
          }] : []),
        ]}
      />

      <Stack spacing={4}>
        <OrganizationInfoSection organization={organization} />
        <OrganizationBillingSection organization={organization} />
        <OrganizationNotesSection notes={organization.notes} />
        <OrganizationRelationshipsSection
          users={organizationUsers}
          networks={organizationNetworks}
          getUserEditPath={(user) => `/app/consultant/users/edit/${user.id}`}
          networkActionLabel={canAttachToNetwork ? 'Attach school to network' : 'Already attached to network'}
          onNetworkAction={attachModal.onOpen}
          disableNetworkAction={!canAttachToNetwork}
        />
      </Stack>

      <Modal isOpen={attachModal.isOpen} onClose={attachModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Attach to network</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleAttachOrganization}>
            <ModalBody>
              <VStack spacing={3}>
                <Select value={attachForm.network_id} onChange={(e) => setAttachForm((prev) => ({ ...prev, network_id: e.target.value }))}>
                  <option value="">Select network</option>
                  {networks.map((network) => (
                    <option key={network.id} value={network.id}>{network.name}</option>
                  ))}
                </Select>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={attachModal.onClose}>Cancel</Button>
              <Button type="submit" colorScheme="blue" isLoading={isSubmittingAttach} isDisabled={!attachForm.network_id}>
                Attach
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ConsultantOrganizationView;
